/**
 * Utility functions for working with event dates
 */

import { Events, DraftEvents } from "@/lib/database/events";
import type { DateLocale } from "@/lib/date-format/date-format";
import { parseLocalDate } from "@/lib/utils/date-utils";

export type EventDateOption = {
  date?: string; // For single-day events (YYYY-MM-DD)
  startTime?: string; // For single-day events (HH:MM)
  endTime?: string; // For single-day events (HH:MM)
  startDate?: string; // For multi-day events (YYYY-MM-DD)
  endDate?: string; // For multi-day events (YYYY-MM-DD)
};

// Type that accepts both Events and DraftEvents
export type EventWithDates = Events | DraftEvents | { event_dates?: EventDateOption[] };

/**
 * Get the preferred (first) event date option from event_dates field
 * @param event - The event object with date fields
 * @returns The preferred event date option
 */
export function getPreferredEventDates(event: EventWithDates): EventDateOption | null {
  // Handle if event_dates is a string (JSONB from database)
  let eventDates = event.event_dates;
  if (typeof eventDates === "string") {
    try {
      eventDates = JSON.parse(eventDates);
    } catch (e) {
      console.error("Failed to parse event_dates string:", e);
      return null;
    }
  }

  // If event_dates field exists and has entries, return the first one (highest preference)
  if (eventDates && Array.isArray(eventDates) && eventDates.length > 0) {
    return eventDates[0];
  }

  return null;
}

/**
 * The preferred date option's first and last day, for a compact range label.
 *
 * @param eventDates - `events.event_dates`, or null when the event has none.
 * @returns ISO `YYYY-MM-DD` bounds; both null when there is no preferred option.
 * @throws Never.
 */
export function preferredDateBounds(eventDates: EventDateOption[] | null): {
  startDate: string | null;
  endDate: string | null;
} {
  const option = getPreferredEventDates({ event_dates: eventDates ?? [] });
  return {
    startDate: option?.date ?? option?.startDate ?? null,
    endDate: option?.date ?? option?.endDate ?? null,
  };
}

/**
 * Get all event date options, with the preferred one first
 * @param event - The event object with date fields
 * @returns Array of all event date options
 */
export function getAllEventDateOptions(event: EventWithDates): EventDateOption[] {
  // Handle if event_dates is a string (JSONB from database)
  let eventDates = event.event_dates;
  if (typeof eventDates === "string") {
    try {
      eventDates = JSON.parse(eventDates);
    } catch (e) {
      return [];
    }
  }

  // Return all event date options
  return (Array.isArray(eventDates) ? eventDates : []) as EventDateOption[];
}

/**
 * Format a single event date option for display
 * @param dateOption - The event date option to format
 * @param locale - Date locale; pass `useDateFormat().locale` on the dashboard
 * @returns Formatted string representation
 */
export function formatEventDateOption(dateOption: EventDateOption, locale: DateLocale = "en-US"): string {
  if (dateOption.date) {
    // Single-day event - treat as local date, not UTC
    const date = new Date(dateOption.date + "T00:00:00");
    const formattedDate = date.toLocaleDateString(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    if (dateOption.startTime && dateOption.endTime) {
      return `${formattedDate} (${dateOption.startTime} - ${dateOption.endTime})`;
    } else if (dateOption.startTime) {
      return `${formattedDate} (starts at ${dateOption.startTime})`;
    }
    return formattedDate;
  } else if (dateOption.startDate && dateOption.endDate) {
    // Multi-day event - treat as local dates, not UTC
    const startDate = new Date(dateOption.startDate + "T00:00:00");
    const endDate = new Date(dateOption.endDate + "T00:00:00");

    const formattedStart = startDate.toLocaleDateString(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const formattedEnd = endDate.toLocaleDateString(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    return `${formattedStart} - ${formattedEnd}`;
  }

  return "Dates not specified";
}

/**
 * Format all event date options for display (e.g., in UI)
 * @param event - The event object with date fields
 * @param separator - String to separate multiple date options
 * @returns Formatted string with all date options
 */
export function formatAllEventDateOptions(event: EventWithDates, separator: string = " | "): string {
  const allOptions = getAllEventDateOptions(event);

  if (allOptions.length === 0) {
    return "Dates not specified";
  }

  return allOptions.map((option) => formatEventDateOption(option)).join(separator);
}

/**
 * Format event dates for display in RFP emails with HTML formatting
 * @param event - The event object with date fields
 * @returns HTML formatted string
 */
export function formatEventDatesForRFP(event: EventWithDates): string {
  const allOptions = getAllEventDateOptions(event);

  if (allOptions.length === 0) {
    return "Dates to be determined";
  }

  if (allOptions.length === 1) {
    return formatEventDateOption(allOptions[0]);
  }

  // Multiple date options - format with preferences
  const formattedOptions = allOptions.map((option, index) => {
    const formatted = formatEventDateOption(option);
    if (index === 0) {
      return `<strong>Preferred:</strong> ${formatted}`;
    }
    return `Option ${index + 1}: ${formatted}`;
  });

  return formattedOptions.join("<br>");
}

/**
 * Format event dates for Slack notifications
 * @param event - The event object with date fields
 * @returns Formatted string for Slack
 */
export function formatEventDatesForSlack(event: EventWithDates): string {
  const allOptions = getAllEventDateOptions(event);

  if (allOptions.length === 0) {
    return "Dates not specified";
  }

  if (allOptions.length === 1) {
    return formatEventDateOption(allOptions[0]);
  }

  // Multiple date options
  const formattedOptions = allOptions.map((option, index) => {
    const formatted = formatEventDateOption(option);
    if (index === 0) {
      return `*Preferred:* ${formatted}`;
    }
    return `Option ${index + 1}: ${formatted}`;
  });

  return formattedOptions.join("\n");
}

/**
 * Convert a single event date option to start/end `Date` objects, parsing
 * `YYYY-MM-DD` strings at local noon so they never shift across timezones.
 * @param option - The event date option to convert (single- or multi-day)
 * @returns Object with startDate and endDate as Date objects, or null if unparseable
 */
export function eventDateOptionToRange(option: EventDateOption | null): { startDate: Date; endDate: Date } | null {
  if (!option) {
    return null;
  }

  // Single-day event
  if (option.date) {
    const date = parseLocalDate(option.date);
    if (!isNaN(date.getTime())) {
      return { startDate: date, endDate: date };
    }
  }

  // Multi-day event
  if (option.startDate && option.endDate) {
    const startDate = parseLocalDate(option.startDate);
    const endDate = parseLocalDate(option.endDate);
    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
      return { startDate, endDate };
    }
  }

  return null;
}

/**
 * Get the start and end dates as Date objects from the preferred (first) event
 * date option. Useful for date calculations and comparisons.
 * @param event - The event object with date fields
 * @returns Object with startDate and endDate as Date objects, or null
 */
export function getEventDateRange(event: EventWithDates): { startDate: Date; endDate: Date } | null {
  return eventDateOptionToRange(getPreferredEventDates(event));
}

/**
 * Get the number of nights for the event
 * @param event - The event object with date fields
 * @returns Number of nights, or 0 for single-day events
 */
export function getEventNights(event: EventWithDates): number {
  const dateRange = getEventDateRange(event);

  if (!dateRange) {
    return 0;
  }

  const msPerDay = 24 * 60 * 60 * 1000;
  const nights = Math.round((dateRange.endDate.getTime() - dateRange.startDate.getTime()) / msPerDay);

  return Math.max(0, nights);
}

/**
 * Check if the event has multiple date options
 * @param event - The event object with date fields
 * @returns True if there are multiple date options
 */
export function hasMultipleDateOptions(event: EventWithDates): boolean {
  return (event.event_dates?.length ?? 0) > 1;
}

/**
 * How a submitted quote's dates relate to the event's date options.
 * - `primary`: matches the preferred (first) option
 * - `alternate`: matches a later (alternate) option
 * - `off-list`: matches none of the event's options
 * - `unknown`: the event has no date options on file to compare against
 */
export type QuoteDateKind = "primary" | "alternate" | "off-list" | "unknown";

/**
 * Summary of how a submitted quote's dates relate to the event's date options.
 */
export type QuoteDateSummary = {
  /** How the quote's dates relate to the event's date options. */
  kind: QuoteDateKind;
  /** The quote's submitted dates formatted for display, e.g. "Sep 27, 2026 - Oct 2, 2026". */
  formattedDates: string;
};

/**
 * Normalize a date value to its `YYYY-MM-DD` prefix, tolerating full ISO datetimes
 * (some quote-ingestion paths store `start_date`/`end_date` with a time component).
 * @param value - A date string, expected to start with `YYYY-MM-DD`
 * @returns The first 10 characters (`YYYY-MM-DD`)
 */
function toDateOnly(value: string): string {
  return value.slice(0, 10);
}

/**
 * Normalize an event date option to a comparable start/end pair of YYYY-MM-DD strings.
 * @param option - The event date option (single-day `date` or multi-day `startDate`/`endDate`)
 * @returns The start/end pair, or null when the option has no usable dates
 */
function eventDateOptionRange(option: EventDateOption): { start: string; end: string } | null {
  if (option.startDate && option.endDate)
    return { start: toDateOnly(option.startDate), end: toDateOnly(option.endDate) };
  if (option.date) return { start: toDateOnly(option.date), end: toDateOnly(option.date) };
  return null;
}

/**
 * Classify a submitted quote's dates against the event's date options.
 *
 * The quote's dates are compared to each `event_dates` option, where index 0 is the
 * preferred/"primary" option and later indices are alternates. Useful for surfacing which
 * dates a venue quoted for without opening the quote.
 *
 * @param event - The event whose `event_dates` options the quote is compared against
 * @param startDate - The quote's submitted start date (YYYY-MM-DD), from `extraction_data.start_date`
 * @param endDate - The quote's submitted end date (YYYY-MM-DD), from `extraction_data.end_date`
 * @returns A summary of how the quote relates to the event's dates, or null when the quote has no dates
 */
export function classifyQuoteDates(
  event: EventWithDates,
  startDate: string | null | undefined,
  endDate: string | null | undefined
): QuoteDateSummary | null {
  if (!startDate || !endDate) return null;

  const start = toDateOnly(startDate);
  const end = toDateOnly(endDate);
  const formattedDates = formatEventDateOption(start === end ? { date: start } : { startDate: start, endDate: end });

  const options = getAllEventDateOptions(event);
  if (options.length === 0) return { kind: "unknown", formattedDates };

  const optionIndex = options.findIndex((option) => {
    const range = eventDateOptionRange(option);
    return range?.start === start && range.end === end;
  });
  const kind: QuoteDateKind = optionIndex === 0 ? "primary" : optionIndex > 0 ? "alternate" : "off-list";

  return { kind, formattedDates };
}
